import React, {useState} from 'react'
import {Modal} from 'react-bootstrap'
import {KTIcon} from '../../../_metronic/helpers'
import DashboardApiTableShow, {DashboardColumn} from './DashboardApiTableShow'
import {FbaFbmAdjustedProductItem} from './dashboardTableTypes'
import {
  formatDashboardBoolean,
  formatDashboardCurrency,
  formatDashboardDateTime,
  renderDashboardLinkIcon,
} from './dashboardFormatters'

// A missing (null/0) old price means the product is still at its original price
const hasOldPrice = (value: number | string | null | undefined): boolean => {
  const n = toPrice(value)
  return n !== null && n !== 0
}

const renderOldPrice = (value: number | string | null | undefined): React.ReactNode =>
  hasOldPrice(value) ? formatDashboardCurrency(value) : '-'

const toPrice = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') return null
  const n = Number(value)
  return Number.isNaN(n) ? null : n
}

const SectionLabel: React.FC<{children: React.ReactNode}> = ({children}) => (
  <div className='text-gray-600 fw-bold fs-7 text-uppercase mb-2' style={{letterSpacing: '0.06em'}}>
    {children}
  </div>
)

// TRUE/FALSE pill with a status dot
const StatusPill: React.FC<{value: string | boolean | null | undefined}> = ({value}) => {
  const label = formatDashboardBoolean(value)
  if (label === '-') return <span className='text-gray-600 fw-semibold'>-</span>
  const isTrue = label === 'TRUE'
  return (
    <span
      className={`d-inline-flex align-items-center gap-2 rounded-pill px-3 py-1 fs-7 fw-bold ${
        isTrue ? 'bg-light-success text-success' : 'bg-light-danger text-danger'
      }`}
    >
      <span
        className={`rounded-circle ${isTrue ? 'bg-success' : 'bg-danger'}`}
        style={{width: 7, height: 7, display: 'inline-block'}}
      />
      {label}
    </span>
  )
}

const PriceCard: React.FC<{label: string; value: number | string | null | undefined; empty?: boolean}> = ({
  label,
  value,
  empty,
}) => (
  <div className='border border-gray-300 rounded p-4 h-100'>
    <div className='text-gray-600 fw-semibold fs-6 mb-1'>{label}</div>
    <div className='text-gray-900 fw-bolder fs-2'>{empty ? '-' : formatDashboardCurrency(value)}</div>
  </div>
)

// "Price increased by $0.03 (1.0%)" - hidden when either price is missing.
const PriceDifference: React.FC<{oldPrice: number | null; newPrice: number | null}> = ({oldPrice, newPrice}) => {
  if (newPrice === null) return null
  if (oldPrice === null || oldPrice === 0) {
    return <div className='text-gray-600 fs-6 mt-3'>No previous price - showing the original price.</div>
  }
  if (oldPrice === newPrice) {
    return <div className='text-gray-600 fs-6 mt-3'>Price did not change.</div>
  }
  const diff = Math.abs(newPrice - oldPrice)
  const percent = oldPrice !== 0 ? (diff / oldPrice) * 100 : null
  const higher = newPrice > oldPrice
  return (
    <div className='d-flex align-items-center gap-2 fs-6 text-gray-700 mt-3'>
      <svg
        width='16'
        height='16'
        viewBox='0 0 24 24'
        fill='none'
        stroke={higher ? '#6fcf97' : '#f08a8a'}
        strokeWidth='2.5'
        strokeLinecap='round'
        strokeLinejoin='round'
        aria-hidden='true'
      >
        {higher ? <path d='M12 20V4M5 11l7-7 7 7' /> : <path d='M12 4v16M5 13l7 7 7-7' />}
      </svg>
      <span>
        Price {higher ? 'increased' : 'decreased'} by{' '}
        <span className={`fw-bold ${higher ? 'text-success' : 'text-danger'}`}>
          {formatDashboardCurrency(diff)}
          {percent !== null ? ` (${percent.toFixed(1)}%)` : ''}
        </span>
      </span>
    </div>
  )
}

const DetailListRow: React.FC<{label: string; children: React.ReactNode; last?: boolean}> = ({
  label,
  children,
  last,
}) => (
  <div
    className={`d-flex justify-content-between align-items-center px-4 py-3 ${
      last ? '' : 'border-bottom border-gray-300'
    }`}
  >
    <span className='text-gray-600 fw-semibold fs-6'>{label}</span>
    {children}
  </div>
)

const ViewDetailsCell: React.FC<{item: FbaFbmAdjustedProductItem}> = ({item}) => {
  const [show, setShow] = useState(false)

  return (
    <>
      <button
        type='button'
        className='btn btn-sm btn-light-primary d-inline-flex align-items-center'
        title='View details'
        onClick={(e) => {
          e.currentTarget.blur()
          setShow(true)
        }}
      >
        <KTIcon iconName='eye' className='fs-3 me-1' />
        Details
      </button>
      <Modal show={show} onHide={() => setShow(false)} centered restoreFocus={false}>
        <Modal.Header closeButton className='border-bottom'>
          <div className='d-flex align-items-center gap-3'>
            <span
              className='d-inline-flex align-items-center justify-content-center rounded bg-light-primary text-primary'
              style={{width: 40, height: 40}}
            >
              <KTIcon iconName='price-tag' className='fs-2 text-primary' />
            </span>
            <div>
              <Modal.Title className='fs-3 mb-0'>Adjusted Price Details</Modal.Title>
              <div className='text-gray-600 fs-6'>
                <span className='text-gray-800 fw-semibold'>{item.sku}</span> &bull; {item.channel}
              </div>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <SectionLabel>Price Comparison</SectionLabel>
          <div className='row g-3'>
            <div className='col-6'>
              <PriceCard label='Old Price' value={item.old_price} empty={!hasOldPrice(item.old_price)} />
            </div>
            <div className='col-6'>
              <PriceCard label='New Price' value={item.price} />
            </div>
          </div>
          <PriceDifference oldPrice={toPrice(item.old_price)} newPrice={toPrice(item.price)} />

          <div className='mt-6'>
            <SectionLabel>Details</SectionLabel>
            <div className='border border-gray-300 rounded'>
              <DetailListRow label='Fulfillment Channel'>
                {item.fulfillment_channel?.toLowerCase().includes('amazon') ? (
                  <div className='lh-sm text-end'>
                    <div className='text-gray-800 fw-semibold'>FBA</div>
                    <div className='text-gray-600 fs-8'>{item.fulfillment_channel}</div>
                  </div>
                ) : (
                  <span className='text-gray-800 fw-semibold'>{item.fulfillment_channel ?? '-'}</span>
                )}
              </DetailListRow>
              <DetailListRow label='SFP Status'>
                <StatusPill value={item.sfp_status} />
              </DetailListRow>
              <DetailListRow label='SFP Percentage'>
                <span className='text-gray-800 fw-semibold'>{item.sfp_percentage ?? '-'}%</span>
              </DetailListRow>
              <DetailListRow label='Type' last>
                <span className='text-gray-800 fw-semibold'>{item.type ?? '-'}</span>
              </DetailListRow>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type='button' className='btn btn-light' onClick={() => setShow(false)}>
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

const columns: DashboardColumn<FbaFbmAdjustedProductItem>[] = [
  {header: 'SKU', sortKey: 'sku', minWidth: 120, render: (item) => <span className='font-monospace fw-semibold'>{item.sku ?? '-'}</span>},
  {header: 'Channel', sortKey: 'channel', render: (item) => item.channel ?? '-'},
  {header: 'Fulfillment', sortKey: 'fulfillment_channel', minWidth: 110, render: (item) => {
      const channel = item.fulfillment_channel
      if (!channel) return '-'
      if (!channel.toLowerCase().includes('amazon')) return channel
      return (
        <div className='lh-sm'>
          <div className='fw-semibold'>FBA</div>
          <div className='text-gray-600 fs-8'>{channel}</div>
        </div>
      )
    },
  },
  {header: 'SFP Status', sortKey: 'sfp_status', width: 120, render: (item) => <StatusPill value={item.sfp_status} />},
  {header: 'Old Price', sortKey: 'old_price', width: 100, render: (item) => renderOldPrice(item.old_price)},
  {header: 'New Price', sortKey: 'price', width: 100, render: (item) => formatDashboardCurrency(item.price)},
  {
    header: 'Changed At',
    sortKey: 'changed_at',
    width: 150,
    render: (item) => {
      const [datePart, timePart] = formatDashboardDateTime(item.changed_at).split(', ')
      return (
        <div className='lh-sm'>
          <div className='fw-semibold'>{datePart}</div>
          {timePart && <div className='text-gray-600 fs-8'>{timePart}</div>}
        </div>
      )
    },
  },
  {
    header: 'Action',
    width: 180,
    render: (item) => (
      <div className='d-flex align-items-center gap-2'>
        {renderDashboardLinkIcon(item.broken_link)}
        <ViewDetailsCell item={item} />
      </div>
    ),
  },
]

function FbaFbmAdjustedProductsShow() {
  return (
    <DashboardApiTableShow<FbaFbmAdjustedProductItem>
      endpoint='FbaFbmAdjustedProducts'
      title='SFP Percentage for FBA/FBM-Adjusted Products'
      columns={columns}
    />
  )
}

export default FbaFbmAdjustedProductsShow
